import { skipToken, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  getV1CompaniesCompanyIdEquipmentTypesTypeahead,
  GetV1CompaniesCompanyIdEquipmentTypesTypeaheadData,
  getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipmentTypesTypeahead,
  GetV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipmentTypesTypeaheadData,
} from '@treadinc/horizon-api-spec';
import { useCallback, useMemo, useState } from 'react';

import { extractPagination } from '~services/pagination';

import {
  InfiniteQuery,
  InfiniteQueryFetchReason,
  InfiniteQueryParamsState,
} from './constants';
import { deriveInfiniteQueryBaseParams } from './utils';

export default function useEquipmentTypesInfiniteQuery(
  queryKey: string,
  companyId: string,
  managedCompanyId?: string,
): InfiniteQuery {
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<InfiniteQueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId, managedCompanyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    // Without a company id the `{company-id}` path placeholder is left
    // unsubstituted and the request 404s (HORIZON-FE-1RY). Skip the fetch.
    queryFn:
      queryParams.shouldFetch && companyId
        ? async ({ pageParam, signal }) => {
            const query:
              | GetV1CompaniesCompanyIdEquipmentTypesTypeaheadData['query']
              | GetV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipmentTypesTypeaheadData['query'] =
              deriveInfiniteQueryBaseParams({ ...queryParams, ...pageParam });

            const response = managedCompanyId
              ? await getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdEquipmentTypesTypeahead(
                  {
                    path: {
                      'company-id': companyId,
                      'managed-company-id': managedCompanyId,
                    },
                    query,
                    signal,
                  },
                )
              : await getV1CompaniesCompanyIdEquipmentTypesTypeahead({
                  path: { 'company-id': companyId },
                  query,
                  signal,
                });

            const equipmentTypes = response.data.data.map((equipmentType) => {
              return { label: equipmentType.name, value: equipmentType.id };
            });
            const pagination = extractPagination(response);

            setQueryParams((state) => ({
              ...state,
              pagination: { ...pagination, hasMore: Boolean(pagination.after) },
            }));

            return equipmentTypes;
          }
        : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const fetchReason = useMemo(() => {
    if (isFetchingNextPage) {
      return InfiniteQueryFetchReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return InfiniteQueryFetchReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, [queryKey]);

  return { clear, data: allData, fetch, fetchMore, fetchReason, isDummyQuery: false };
}
